import { format, isValid, parseISO } from "date-fns";
import { formatRegisteredByDisplay } from "./visitorHelpers";
import { resolveVisitorStatus } from "./visitorStatus";

export const INITIAL_VISITOR_DIRECTORY_FILTERS = {
  start_date: "",
  end_date: "",
  visitor_id: "",
  exact_entry_date: "",
  visitor_name: "",
  mobile_number: "",
  purpose_name: "",
  person_to_meet_name: "",
  in_time: "",
  out_time: "",
  status: "",
  created_by_name: "",
  page: 1,
};

function entryDateKey(row) {
  const v = row?.entry_date;
  if (!v) return "";
  return String(v).slice(0, 10);
}

function formatDateDisplay(value) {
  if (!value) return "";
  try {
    const d = parseISO(String(value).slice(0, 10));
    return isValid(d) ? format(d, "dd MMM yyyy") : String(value).slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

function padTime(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function visitorIdText(row) {
  return String(row?.visitor_code || row?.id || "").trim();
}

function inDateRange(row, start, end) {
  const key = entryDateKey(row);
  if (!key) return false;
  if (start && key < start) return false;
  if (end && key > end) return false;
  return true;
}

function matchesField(row, filters) {
  if (filters.visitor_id) {
    const needle = String(filters.visitor_id).toLowerCase();
    if (!visitorIdText(row).toLowerCase().includes(needle)) return false;
  }
  if (filters.exact_entry_date && entryDateKey(row) !== filters.exact_entry_date) {
    return false;
  }
  if (filters.visitor_name) {
    const n = String(row.visitor_name || "").toLowerCase();
    if (!n.includes(String(filters.visitor_name).toLowerCase())) return false;
  }
  if (filters.mobile_number) {
    if (!String(row.mobile_number || "").includes(String(filters.mobile_number))) return false;
  }
  if (filters.purpose_name) {
    if (
      String(row.purpose_name || "").toLowerCase() !==
      String(filters.purpose_name).toLowerCase()
    ) {
      return false;
    }
  }
  if (filters.person_to_meet_name) {
    if (
      String(row.person_to_meet_name || "").toLowerCase() !==
      String(filters.person_to_meet_name).toLowerCase()
    ) {
      return false;
    }
  }
  if (filters.in_time && padTime(row.in_time) !== padTime(filters.in_time)) return false;
  if (filters.out_time && padTime(row.out_time) !== padTime(filters.out_time)) return false;
  if (filters.status && resolveVisitorStatus(row) !== filters.status) return false;
  if (filters.created_by_name) {
    if (
      String(row.created_by_name || "").toLowerCase() !==
      String(filters.created_by_name).toLowerCase()
    ) {
      return false;
    }
  }
  return true;
}

export function applyVisitorDirectoryFilters(rows, filters) {
  const start = String(filters.start_date || "").trim();
  const end = String(filters.end_date || "").trim();
  return (rows || []).filter((row) => {
    if (start || end) {
      if (!inDateRange(row, start, end)) return false;
    }
    return matchesField(row, filters);
  });
}

export function hasActiveVisitorDirectoryFilters(filters) {
  const f = filters || {};
  return Boolean(
    f.start_date ||
      f.end_date ||
      f.visitor_id ||
      f.exact_entry_date ||
      f.visitor_name ||
      f.mobile_number ||
      f.purpose_name ||
      f.person_to_meet_name ||
      f.in_time ||
      f.out_time ||
      f.status ||
      f.created_by_name,
  );
}

export function buildVisitorExportRows(rows, columnDefs) {
  const cols = (columnDefs || []).filter((c) => c.id !== "actions");
  return (rows || []).map((row, idx) => {
    const record = { "S.No": idx + 1 };
    for (const col of cols) {
      switch (col.id) {
        case "visitor_id":
          record[col.label] = visitorIdText(row);
          break;
        case "entry_date":
          record[col.label] = formatDateDisplay(row.entry_date);
          break;
        case "visitor_name":
          record[col.label] = row.visitor_name || "";
          break;
        case "mobile_number":
          record[col.label] = row.mobile_number || "";
          break;
        case "purpose_name":
          record[col.label] = row.purpose_name || "";
          break;
        case "person_to_meet_name":
          record[col.label] = row.person_to_meet_name || "";
          break;
        case "in_time":
          record[col.label] = padTime(row.in_time);
          break;
        case "out_time":
          record[col.label] = padTime(row.out_time);
          break;
        case "created_by_name":
          record[col.label] = formatRegisteredByDisplay(row);
          break;
        case "status":
          record[col.label] = resolveVisitorStatus(row);
          break;
        default:
          record[col.label] = row[col.id] ?? "";
      }
    }
    return record;
  });
}

export { formatDateDisplay, padTime, entryDateKey, visitorIdText };
